import React, {createContext, useContext, useMemo, useReducer} from 'react';
import {ACTIONS, informeReducer, initialState, nextUndoId} from './informeReducer';
import {hasUnsavedWork, isInformeDirty, isSheetModified, isTemplateModified} from './canonical';

const InformeContext = createContext(null);

/**
 * El estado de la pantalla de Informes: un `useReducer` sobre `informeReducer` (puro), más los
 * derivados (los dos dirties) y las acciones con nombre. Los modales y el header leen de acá.
 *
 * Las acciones son ESTABLES (memo sin deps: `dispatch` no cambia): antes se recreaban con cada
 * `state`, y todo efecto que las llevara en sus deps —los timers del flash de cuentas, por
 * ejemplo— se re-armaba en cada tecla (review 05/09, C-informes-43).
 */
export function InformeProvider({agencyClientId, children}) {
    const [state, dispatch] = useReducer(informeReducer, agencyClientId, initialState);

    const actions = useMemo(() => ({
        setClient: (clientId) => dispatch({type: ACTIONS.SET_CLIENT, clientId}),
        // `fieldsData` es el catálogo del Explorador: con él se elige la dimensión con la que
        // nacen los gráficos (la primera GRAFICABLE, ver `chartFields.js`). Va por parámetro y no
        // por prop del provider para que las acciones sigan siendo estables.
        newInforme: (fields, fieldsData, kpis = null) => dispatch({type: ACTIONS.NEW_INFORME, fields, fieldsData, kpis}),
        loadInforme: (informe) => dispatch({type: ACTIONS.LOAD_INFORME, informe}),
        setName: (name) => dispatch({type: ACTIONS.SET_NAME, name}),
        setMode: (mode) => dispatch({type: ACTIONS.SET_MODE, mode}),
        setSettings: (patch) => dispatch({type: ACTIONS.SET_SETTINGS, patch}),
        applyFields: (fields, fieldsData) => dispatch({type: ACTIONS.FIELDS_APPLY, fields, fieldsData}),
        applyTableColumns: (uid, fields) => dispatch({type: ACTIONS.TABLE_COLUMNS_APPLY, uid, fields}),
        applyTemplate: (template) => dispatch({type: ACTIONS.APPLY_TEMPLATE, template}),
        addPanel: (panelType, at, fieldsData) => dispatch({type: ACTIONS.PANEL_ADD, panelType, at, fieldsData}),
        // Un panel que nace armado (`presets.js`): `fields` son sus columnas, que entran a la paleta.
        addPresetPanel: (panel, fields, at) => dispatch({type: ACTIONS.PANEL_ADD, panel, fields, at}),
        // Devuelve el id de la entrada de Deshacer: el toast lo lleva y `undo(entryId)` sólo
        // deshace ESA acción si sigue siendo la última (C-informes-17).
        removePanel: (uid) => {
            const entryId = nextUndoId();
            dispatch({type: ACTIONS.PANEL_REMOVE, uid, entryId});
            return entryId;
        },
        movePanel: (from, to) => dispatch({type: ACTIONS.PANEL_MOVE, from, to}),
        updatePanel: (uid, patch) => dispatch({type: ACTIONS.PANEL_UPDATE, uid, patch}),
        renamePanel: (uid, title) => dispatch({type: ACTIONS.PANEL_RENAME, uid, title}),
        undo: (entryId) => dispatch({type: ACTIONS.UNDO, entryId}),
        // `sent`: lo que VIAJÓ al backend ({settings, template}); el baseline se calcula sobre eso y
        // no sobre el estado actual, que pudo cambiar mientras el request estaba en vuelo.
        savedInforme: (informe, sent) => dispatch({type: ACTIONS.SAVE_INFORME_OK, informe, sent}),
        savedTemplate: (template) => dispatch({type: ACTIONS.SAVE_TEMPLATE_OK, template}),
        // Publicar/despublicar NO es guardar: sólo toca `is_published`, nunca el baseline.
        publishOk: (isPublished) => dispatch({type: ACTIONS.PUBLISH_OK, isPublished}),
        clearAccountsReset: () => dispatch({type: ACTIONS.CLEAR_ACCOUNTS_RESET}),
        // [Dejar así] ante el aviso de GA4 en filas aparte, para esa tabla del informe abierto.
        keepGa4Split: (uid) => dispatch({type: ACTIONS.GA4_SPLIT_KEEP, uid}),
    }), []);

    const value = useMemo(() => ({
        state,
        dispatch,
        actions,
        informeDirty: isInformeDirty(state),
        templateModified: isTemplateModified(state),
        // El tercer derivado, y NO la suma de los otros dos: los dirties dicen «no está guardado»
        // (el rótulo del header), éste dice «hay trabajo que se perdería» (los guards). Va acá y no
        // en la página porque se calcula con un `JSON.stringify` de la plantilla: en el memo corre
        // cuando cambia el estado, y no en cada render que abre un modal (card eo7J0qds).
        unsavedWork: hasUnsavedWork(state),
        // «Cambió la hoja», que NO es «cambió la plantilla»: sin `template.id` la segunda es siempre
        // `false` y el modal no nombraba los paneles que se iban a perder (review C-informes-06).
        sheetModified: isSheetModified(state),
    }), [state, actions]);

    return <InformeContext.Provider value={value}>{children}</InformeContext.Provider>;
}

export function useInforme() {
    const ctx = useContext(InformeContext);
    if (!ctx) throw new Error('useInforme() fuera de <InformeProvider>');
    return ctx;
}
